import { BaseReactiveClass } from 'Classes/BaseReactiveClass';
import PROFILE_CONST from 'Const/profile.json';
import INVESTMENT_CONST from 'Const/investment.json';
import * as ACCREDITATION_METHODS from 'Config/accreditation-methods';
import * as accountApi from 'Api/account';
import { getShortDate } from 'Utils/time';
import {
  ModelAccountWallet,
} from 'Components/AccountWallet/models';
import { ModelAccountSuitability } from './ModelAccountSuitability';
import { ModelAccountBeneficialOwnerItem } from './ModelAccountBeneficialOwnerItem';
import { ModelAccountController } from './ModelAccountController';
import { ModelAccountTrusteeItem } from './ModelAccountTrusteeItem';

const { KYC_STATUS, SM_STATUS } = PROFILE_CONST;

const {
  LLC_TYPES,
  PROFILE_TYPES,
  ACCREDITATION_PROFILE_STATUS,
  ENTITY_TYPES,
  REPORTING_ACCOUNT_TYPES,
} = INVESTMENT_CONST;

export class ModelAccount extends BaseReactiveClass {
  static TYPE_INDIVIDUAL = PROFILE_TYPES.INDIVIDUAL;
  static TYPE_ENTITY = PROFILE_TYPES.ENTITY;
  static TYPE_TRUST = PROFILE_TYPES.TRUST;
  static TYPE_SDIRA = PROFILE_TYPES.SDIRA;
  static TYPE_SOLO401K = PROFILE_TYPES.SOLO401K;
  static TYPE_SOLO = ModelAccount.TYPE_SOLO401K;

  static KYC_STATUS_NEW = KYC_STATUS.new;
  static KYC_STATUS_ACTIVE = KYC_STATUS.active;
  static KYC_STATUS_SUCCESS = KYC_STATUS.success;
  static KYC_STATUS_FAILED = KYC_STATUS.failed;
  static KYC_STATUS_EXPIRED = KYC_STATUS.expired;
  static KYC_STATUS_CANCELED = KYC_STATUS.canceled;
  static KYC_STATUS_PENDING_REVIEW = KYC_STATUS.pending_review;

  static KYB_STATUS_APPROVED = 'approved';
  static KYB_STATUS_PENDING = 'pending';
  static KYB_STATUS_REJECTED = 'rejected';

  static KYC_COMMON_STATUS_APPROVED = 'approved';
  static KYC_COMMON_STATUS_PENDING = 'pending';

  static ACCREDITATION_STATUS_APPROVED = ACCREDITATION_PROFILE_STATUS.APPROVED;
  static ACCREDITATION_STATUS_NEED_MORE_INFO = ACCREDITATION_PROFILE_STATUS.NEED_MORE_INFO;
  static ACCREDITATION_STATUS_REJECTED = ACCREDITATION_PROFILE_STATUS.REJECTED;
  static ACCREDITATION_STATUS_NEW_INFO_ADDED = ACCREDITATION_PROFILE_STATUS.NEW_INFO_ADDED;
  static ACCREDITATION_STATUS_PENDING = ACCREDITATION_PROFILE_STATUS.PENDING;
  static ACCREDITATION_STATUS_EXPIRED = ACCREDITATION_PROFILE_STATUS.EXPIRED;
  static ACCREDITATION_STATUS_EXPIRED_SOON = ACCREDITATION_PROFILE_STATUS.EXPIRED_SOON;

  static WALLET_STATUS_NEW = 'new';
  static WALLET_STATUS_DEACTIVATED = 'deactivated';
  static WALLET_STATUS_DOCUMENT = 'document';
  static WALLET_STATUS_REACTIVATED = 'reactivated';
  static WALLET_STATUS_RETRY = 'retry';
  static WALLET_STATUS_SUSPENDED = 'suspended';
  static WALLET_STATUS_UNVERIFIED = 'unverified';
  static WALLET_STATUS_NEW_SENT = ModelAccount.WALLET_STATUS_UNVERIFIED;
  static WALLET_STATUS_VERIFIED = 'verified';
  static WALLET_STATUS_DOCUMENT_SENT = 'document_sent';
  static WALLET_STATUS_RETRY_SENT = 'retry_sent';
  static WALLET_STATUS_PENDING = 'pending'; // status not on dwolla but on backend means wallet activation is in progress on background

  static WALLET_BENEFICIAL_OWNERS_CERTIFY_STATUS_CERTIFIED = 'certified';
  static WALLET_BENEFICIAL_OWNERS_CERTIFY_STATUS_RECERTIFY = 'recertify';
  static WALLET_BENEFICIAL_OWNERS_CERTIFY_STATUS_UNCERTIFIED = 'uncertified';

  static SUITABILITY_STATUS_ALLOWED = 'allowed';
  static SUITABILITY_STATUS_INACTIVE = 'inactive';
  static SUITABILITY_STATUS_RESTRICTED = 'restricted';

  static EMPLOYER_CONSENT_APPROVED = 'approved';
  static EMPLOYER_CONSENT_INACTIVE = 'inactive';
  static EMPLOYER_CONSENT_PENDING = 'pending';

  static MARKET_STATUS_INACTIVE = SM_STATUS.INACTIVE;
  static MARKET_STATUS_PENDING = SM_STATUS.PENDING;
  static MARKET_STATUS_APPROVED = SM_STATUS.APPROVED;
  static MARKET_STATUS_DECLINED = SM_STATUS.DECLINED;

  static TRUST_TYPE_IRREVOCABLE = 'Irrevocable Trust';
  static ACCREDITATION_ATTESTATION_STATUS_PENDING = 'pending';
  static ACCREDITATION_ATTESTATION_STATUS_PENDING_FIVE_DAYS = 'pending-5';
  static ACCREDITATION_ATTESTATION_STATUS_PENDING_TEN_DAYS = 'pending-10';

  constructor(data) {
    super(data);

    this.wallet = new ModelAccountWallet({ accountId: this.id });
    this.suitability = new ModelAccountSuitability({ accountId: this.id });
  }

  get typeTitle() {
    return this.isTypeSolo ? 'solo 401k' : this.type;
  }

  get isCanDelete() {
    return Boolean(this.can_delete);
  }

  get title() {
    if (!this.id) return '';
    if (this.isTypeIndividual) return 'Individual Investment Account';
    if (this.isTypeSdira) {
      if (this.ownership?.custodian_full_account_name) return `${this.ownership.custodian_full_account_name}`;
      return `${this.ownership.custodian} ${this.personal.first_name} ${this.personal.last_name} Investment Account`;
    }
    if (this.ownership) {
      if (this.isTypeTrust) return `${this.ownership.trust_name} Investment Account`;
      if (this.isTypeSolo) return `${this.ownership.entity_name} Investment Account`;
      if (this.isTypeEntity) {
        if (this.isEntityLLCTypeDisregardedEntity) {
          return `${this.ownership.disregarded_entity.name} Investment Account`;
        }
        return `${this.ownership.entity_name} Investment Account`;
      }
    }
    return `${this.personal.first_name} ${this.personal.last_name} Investment Account`;
  }

  get accreditationStatus() {
    return this.accreditation_status;
  }

  get isTypeIndividual() {
    return this.type === ModelAccount.TYPE_INDIVIDUAL;
  }

  get isTypeSolo() {
    return this.type === ModelAccount.TYPE_SOLO401K;
  }

  get isTypeTrust() {
    return this.type === ModelAccount.TYPE_TRUST;
  }

  get isTypeSdira() {
    return this.type === ModelAccount.TYPE_SDIRA;
  }

  get isTypeEntity() {
    return this.type === ModelAccount.TYPE_ENTITY;
  }

  get trustType() {
    return this.ownership?.type_of_trust;
  }

  get isTrustTypeIrrevocable() {
    return this.isTypeTrust && this.trustType === ModelAccount.TRUST_TYPE_IRREVOCABLE;
  }

  get entityType() {
    return this.ownership?.type_of_entity;
  }

  get isEntityTypeLLC() {
    return this.isTypeEntity && this.entityType === ENTITY_TYPES.LLC;
  }

  get isEntityTypeCorporation() {
    return this.isTypeEntity && this.entityType === ENTITY_TYPES.Corporation;
  }

  get isEntityTypeLimitedPartnership() {
    return this.isTypeEntity && this.entityType === ENTITY_TYPES['Limited Partnership'];
  }

  get isEntityLLCTypeDisregardedEntity() {
    return this.isEntityTypeLLC && this.ownership.type_of_llc === LLC_TYPES['LLC - Disregarded Entity'];
  }

  get entityLLCDisregardedAccountType() {
    return this.ownership?.disregarded_entity?.reporting_account?.type || '';
  }

  get isEntityLLCDisregardedAccountTypeIndividual() {
    return this.isEntityLLCTypeDisregardedEntity
      && this.entityLLCDisregardedAccountType === REPORTING_ACCOUNT_TYPES.Individual;
  }

  get isEntityLLCDisregardedAccountTypeSdira() {
    return this.isEntityLLCTypeDisregardedEntity
      && this.entityLLCDisregardedAccountType === REPORTING_ACCOUNT_TYPES.SDIRA;
  }

  get isEntityLLCDisregardedAccountTypeSolo() {
    return this.isEntityLLCTypeDisregardedEntity
      && this.entityLLCDisregardedAccountType === REPORTING_ACCOUNT_TYPES['Solo 401K'];
  }

  get isEntityLLCDisregardedAccountTypeTrust() {
    return this.isEntityLLCTypeDisregardedEntity
      && this.entityLLCDisregardedAccountType === REPORTING_ACCOUNT_TYPES.Trust;
  }

  get isEntityLLCDisregardedAccountTypeEntity() { // using in validation
    return this.isEntityLLCTypeDisregardedEntity
      && [
        REPORTING_ACCOUNT_TYPES['LLC Taxed as a Partnership'],
        REPORTING_ACCOUNT_TYPES['LLC Taxed as a C Corp'],
        REPORTING_ACCOUNT_TYPES['LLC Taxed as an S Corp'],
        REPORTING_ACCOUNT_TYPES['S Corporation'],
        REPORTING_ACCOUNT_TYPES['C Corporation'],
        REPORTING_ACCOUNT_TYPES['Qsub Corporation'],
        REPORTING_ACCOUNT_TYPES['Limited Partnership'],
        REPORTING_ACCOUNT_TYPES['General Partnership'],
      ].includes(this.entityLLCDisregardedAccountType);
  }

  get kycStatus() {
    return this.kyc_status;
  }

  get isKYCStatusNew() {
    return this.kycStatus === ModelAccount.KYC_STATUS_NEW || !this.kycStatus;
  }

  get isKYCStatusActive() {
    return this.kycStatus === ModelAccount.KYC_STATUS_ACTIVE;
  }

  get isKYCStatusSuccess() {
    return this.kycStatus === ModelAccount.KYC_STATUS_SUCCESS;
  }

  get isKYCStatusExpired() {
    return this.kycStatus === ModelAccount.KYC_STATUS_EXPIRED;
  }

  get isKYCStatusCanceled() {
    return this.kycStatus === ModelAccount.KYC_STATUS_CANCELED;
  }

  get isKYCStatusPendingReview() {
    return this.kycStatus === ModelAccount.KYC_STATUS_PENDING_REVIEW;
  }

  get isKYCStatusFailed() {
    return this.kycStatus === ModelAccount.KYC_STATUS_FAILED;
  }

  get kybStatus() {
    return this.kyb_status;
  }

  get isKYBStatusEmpty() {
    return !this.kybStatus;
  }

  get isKYBStatusPending() {
    return this.kybStatus === ModelAccount.KYB_STATUS_PENDING;
  }

  get isKYBStatusApproved() {
    return this.kybStatus === ModelAccount.KYB_STATUS_APPROVED;
  }

  get isKYBStatusRejected() {
    return this.kybStatus === ModelAccount.KYB_STATUS_REJECTED;
  }

  get isKYBRequired() {
    // only trust, entity and solo where is_entity_owner = true
    return this.isTypeTrust
      || this.isTypeEntity
      || (this.isTypeSolo && this.isEntityOwner); // TODO: check solo condition if it is handled on backend
  }

  get isKYBOk() {
    return (this.isKYBStatusApproved || !this.isKYBRequired);
  }

  get kycCommonStatus() {
    return this.kyc_common_status;
  }

  get isKYCCommonStatusEmpty() {
    return !this.kycCommonStatus;
  }

  get isKYCCommonStatusPending() {
    return this.kycCommonStatus === ModelAccount.KYC_COMMON_STATUS_PENDING;
  }

  get isKYCCommonStatusApproved() {
    return this.kycCommonStatus === ModelAccount.KYC_COMMON_STATUS_APPROVED;
  }

  get isKYCCommonOk() {
    // check if we have kyc_common_status, it should be approved, if we don't have it, account is ok
    return (this.isKYCCommonStatusEmpty || this.isKYCCommonStatusApproved);
  }

  get businessController() {
    return this.controllers?.find(item => !item.isGrantor) || {};
  }

  get isBusinessControllerExist() {
    return Boolean(this.businessController?.id);
  }

  get isAccreditationApprovedPermanent() {
    return Boolean(this.accreditation_permanent_approved);
  }

  get isAccreditationNew() {
    return (!this.accreditationStatus || this.accreditationStatus === ModelAccount.ACCREDITATION_STATUS_EXPIRED)
      && !this.isAccreditationApprovedPermanent;
  }

  get isAccreditationApproved() {
    return this.isAccreditationApprovedPermanent
      || this.accreditationStatus === ModelAccount.ACCREDITATION_STATUS_APPROVED;
  }

  get isAccreditationPending() {
    return !this.isAccreditationApprovedPermanent
      && [ModelAccount.ACCREDITATION_STATUS_PENDING, ModelAccount.ACCREDITATION_STATUS_NEW_INFO_ADDED]
        .includes(this.accreditationStatus);
  }

  get isAccreditationNeedMoreInfo() {
    return !this.isAccreditationApprovedPermanent
      && [ModelAccount.ACCREDITATION_STATUS_NEED_MORE_INFO, ModelAccount.ACCREDITATION_STATUS_REJECTED]
        .includes(this.accreditationStatus);
  }

  get isAccreditationFilled() {
    return this.isAccreditationApproved || this.isAccreditationNeedMoreInfo || this.isAccreditationExpiredSoon;
  }

  get isAccreditationExpiredSoon() {
    return !this.isAccreditationApprovedPermanent
      && this.accreditationStatus === ModelAccount.ACCREDITATION_STATUS_EXPIRED_SOON;
  }

  get isAccreditationOk() {
    return this.isAccreditationApproved || this.isAccreditationExpiredSoon;
  }

  get accreditationExpiredAfterDayNumber() {
    return this.accreditation_type === 'income' ? 365 : 90;
  }

  get isAccreditationStatusEmpty() {
    return !this.accreditationStatus;
  }

  get accreditationExpiredDate() {
    const { accreditation_date: date } = this;
    if (!date) return null;
    const accreditedPeriodMs = 864e5 * this.accreditationExpiredAfterDayNumber; // 864e5 is 1 day in ms
    return new Date(Date.parse(date) + accreditedPeriodMs);
  }

  get isAccreditationInfoProvided() {
    const accreditation = this?.accreditation;
    if (!accreditation) return false;
    if (accreditation.method === ACCREDITATION_METHODS.LICENSE) {
      return Boolean(accreditation.license?.crd);
    }
    if (accreditation.method === ACCREDITATION_METHODS.ATTESTATION) {
      return Boolean(accreditation?.advisor?.email);
    }
    const files = accreditation.files_upload;
    return Boolean(Array.isArray(files) && files.length);
  }

  get accreditationAttestationStatus() {
    return this.accreditation?.attestation_status;
  }

  get isAccreditationAttestationPending() {
    return this.accreditation?.attestation_status === ModelAccount.ACCREDITATION_ATTESTATION_STATUS_PENDING;
  }

  get isAccreditationAttestationPendingFiveDaysReminder() {
    return this.accreditation?.attestation_status === ModelAccount.ACCREDITATION_ATTESTATION_STATUS_PENDING_FIVE_DAYS;
  }

  get isAccreditationAttestationPendingTenDaysReminder() {
    return this.accreditation?.attestation_status === ModelAccount.ACCREDITATION_ATTESTATION_STATUS_PENDING_TEN_DAYS;
  }

  get isAccreditationMethodAttestation() {
    const accreditation = this?.accreditation;
    return accreditation.method === ACCREDITATION_METHODS.ATTESTATION;
  }

  get isAccreditationAttestation() {
    return this.isAccreditationMethodAttestation
      && (this.isAccreditationAttestationPending
        || this.isAccreditationAttestationPendingFiveDaysReminder
        || this.isAccreditationAttestationPendingTenDaysReminder);
  }

  get accreditationAttestationAdvisor() {
    return this.accreditation?.advisor || {};
  }

  get walletStatus() {
    return this.wallet_status;
  }

  get walletBeneficialOwnersCertifyStatus() {
    return this.certification_status;
  }

  get isWalletStatusEmpty() {
    return !this.walletStatus;
  }

  get isWalletStatusNew() {
    return !this.walletStatus || this.walletStatus === ModelAccount.WALLET_STATUS_NEW;
  }

  get isWalletStatusUnverified() {
    return this.walletStatus === ModelAccount.WALLET_STATUS_UNVERIFIED;
  }

  get isWalletStatusRetry() {
    return this.walletStatus === ModelAccount.WALLET_STATUS_RETRY;
  }

  get isWalletStatusRetrySent() {
    return this.walletStatus === ModelAccount.WALLET_STATUS_RETRY_SENT;
  }

  get isWalletStatusDocument() {
    return this.walletStatus === ModelAccount.WALLET_STATUS_DOCUMENT;
  }

  get isWalletStatusDocumentSent() {
    return this.walletStatus === ModelAccount.WALLET_STATUS_DOCUMENT_SENT;
  }

  get isWalletStatusSuspended() {
    return this.walletStatus === ModelAccount.WALLET_STATUS_SUSPENDED;
  }

  get isWalletStatusVerified() {
    return this.walletStatus === ModelAccount.WALLET_STATUS_VERIFIED;
  }

  get isWalletStatusDeactivated() {
    return this.walletStatus === ModelAccount.WALLET_STATUS_DEACTIVATED;
  }

  get isWalletStatusPending() {
    return this.walletStatus === ModelAccount.WALLET_STATUS_PENDING;
  }

  get isWalletBeneficialOwnersRequired() {
    return this.isTypeEntity;
  }

  get isWalletBeneficialOwnersCertifyStatusCertified() {
    return this.walletBeneficialOwnersCertifyStatus === ModelAccount.WALLET_BENEFICIAL_OWNERS_CERTIFY_STATUS_CERTIFIED;
  }

  get isWalletBeneficialOwnersCertifyRequired() {
    return this.isWalletBeneficialOwnersRequired
      && !this.isWalletBeneficialOwnersCertifyStatusCertified;
  }

  get isWalletActivationComplete() {
    return this.isWalletStatusVerified
      && (!this.isWalletBeneficialOwnersRequired || this.isWalletBeneficialOwnersCertifyStatusCertified);
  }

  get isWalletActivationActionRequired() {
    return this.isWalletStatusNew || this.isWalletStatusRetry || this.isWalletStatusDocument;
  }

  get isWalletActivationWaitingRequired() {
    return this.isWalletStatusUnverified
      || this.isWalletStatusSuspended
      || this.isWalletStatusRetrySent
      || this.isWalletStatusDocumentSent;
  }

  get isWalletActivationReadyForBeneficialOwners() {
    return this.isWalletBeneficialOwnersRequired
      && (this.isWalletStatusVerified || this.isWalletStatusDocumentSent);
  }

  get walletBeneficialOwnersCertifyDate() {
    return this.certification_date || 0;
  }

  get marketStatus() {
    return this.sm_status;
  }

  get isMarketStatusInactive() {
    return this.marketStatus === ModelAccount.MARKET_STATUS_INACTIVE;
  }

  get isMarketStatusPending() {
    return this.marketStatus === ModelAccount.MARKET_STATUS_PENDING;
  }

  get isMarketStatusApproved() {
    return this.marketStatus === ModelAccount.MARKET_STATUS_APPROVED;
  }

  get isMarketStatusDeclined() {
    return this.marketStatus === ModelAccount.MARKET_STATUS_DECLINED;
  }

  get isMarketSellAllowed() {
    return this.isMarketStatusApproved;
  }

  get isMarketBuyAllowed() {
    return this.isMarketStatusApproved
      && this.isAccreditationOk;
  }

  get isMarketAbleToParticipate() {
    // Market is ineligible for accounts from Washington
    return this.personal?.state !== 'WA';
  }

  get isMarketOnboardingAllowed() {
    return this.isKYCStatusSuccess
      && Boolean(this.accreditationStatus);
  }

  get isPlansTrustee() {
    return this.isTypeSolo && Boolean(this.ownership?.plans_trustee);
  }

  get isEntityOwner() {
    return Boolean(this.ownership?.is_entity_owner);
  }

  get useEINForTaxFiling() {
    return Boolean(this.ownership?.use_ein_for_tax_filing);
  }

  get fullNamePersonal() {
    return `${this.personal?.first_name || ''} ${this.personal?.last_name}`;
  }

  get businessName() {
    return this.entityName || '';
  }

  get entityName() {
    if (this.isEntityLLCTypeDisregardedEntity) return this.ownership?.disregarded_entity?.name;
    return this.ownership?.entity_name;
  }

  get entityTitle() {
    if (this.isEntityLLCTypeDisregardedEntity) return this.ownership?.disregarded_entity?.title;
    return this.ownership?.title_entity;
  }

  get entityEIN() {
    if (this.isEntityLLCTypeDisregardedEntity) return this.ownership?.disregarded_entity?.ein;
    return this.ownership?.ein;
  }

  get isSuitabilityStatusInactive() {
    return this.suitability_status === ModelAccount.SUITABILITY_STATUS_INACTIVE;
  }

  get isSuitabilityStatusAllowed() {
    return this.suitability_status === ModelAccount.SUITABILITY_STATUS_ALLOWED;
  }

  get isSuitabilityStatusRestricted() {
    return this.suitability_status === ModelAccount.SUITABILITY_STATUS_RESTRICTED;
  }

  get isNewAccountSuitabilityAndConsentNotOK() {
    return !this.isSuitabilityAndConsentOk
      && this.isAccreditationStatusEmpty && this.isWalletStatusEmpty;
  }

  get isEligibleForInvestment() {
    return this.isSuitabilityAndConsentOk
      && this.isKYCStatusSuccess
      && this.isKYBOk
      && this.isKYCCommonOk
      && !this.isReVerificationRequired;
  }

  get isReVerificationRequired() {
    return this.reverify_account_info;
  }

  get isSuitabilityAndConsentOk() {
    return this.isSuitabilityStatusAllowed;
  }

  get isCustodianAlto() {
    return this.ownership?.custodian === 'Alto';
  }

  get lastUsageDateShort() {
    return getShortDate(this.last_usage_date);
  }

  // TODO: check proper names
  // eslint-disable-next-line camelcase
  set beneficial_owners(data) {
    const beneficialOwners = (data || []).map(item => new ModelAccountBeneficialOwnerItem(item));
    this.$set('_beneficialOwnersList', beneficialOwners);
  }

  set trustees(data) {
    const trustees = (data || []).map(item => new ModelAccountTrusteeItem(item));
    this.$set('_trustees', trustees);
  }

  // TODO: check proper names
  get beneficialOwnersList() {
    return this._beneficialOwnersList?.filter(item => item.isTypeBeneficialOwner) || [];
  }

  get controllers() {
    return this._controllers;
  }

  set controllers(data) {
    const controllers = (data || []).map(item => new ModelAccountController(item));
    this.$set('_controllers', controllers);
  }

  static async fetchData(accountId) {
    const data = await accountApi.getAccount(accountId);
    data.fullLoaded = true;
    return data;
  }

  static async buildClass(accountId) {
    const data = await ModelAccount.fetchData(accountId);
    return new ModelAccount(data);
  }

  async load() {
    if (!this.id) return;
    const data = await ModelAccount.fetchData(this.id);
    this.$setAll(data);
  }

  async update() {
    if (this._updatePromise) return this._updatePromise;
    this._updatePromise = this.load();
    const result = await this._updatePromise;
    this._updatePromise = null;
    return result;
  }

  getUpdatePromise() {
    return this._updatePromise || Promise.resolve();
  }

  async delete() {
    this.$set('$deleteLoading', true);
    try {
      await accountApi.deleteAccount(this.id);
    } finally {
      this.$set('$deleteLoading', false);
    }
  }
}
